import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const THEMES = {
  blue: {
    name: 'blue',
    '--primary':      '#3b82f6',
    '--primary-dark': '#2563eb',
    '--primary-rgb':  '59,130,246',
    '--accent':       '#06b6d4',
    '--bg':           '#0a0f1e',
    '--bg2':          '#0f172a',
    '--bg3':          '#1e293b',
    '--surface':      '#111827',
    '--surface2':     '#1a2436',
    '--border':       '#1e3a5f',
    '--text':         '#e2e8f0',
    '--text-muted':   '#64748b',
    '--gradient':     'linear-gradient(135deg,#3b82f6 0%,#06b6d4 100%)',
    '--glow':         '0 0 20px rgba(59,130,246,0.3)',
  },
  orange: {
    name: 'orange',
    '--primary':      '#f97316',
    '--primary-dark': '#ea580c',
    '--primary-rgb':  '249,115,22',
    '--accent':       '#fbbf24',
    '--bg':           '#0f0800',
    '--bg2':          '#1c1004',
    '--bg3':          '#2d1a08',
    '--surface':      '#1a0f04',
    '--surface2':     '#241508',
    '--border':       '#4a2800',
    '--text':         '#fef3c7',
    '--text-muted':   '#92400e',
    '--gradient':     'linear-gradient(135deg,#f97316 0%,#fbbf24 100%)',
    '--glow':         '0 0 20px rgba(249,115,22,0.3)',
  },
  green: {
    name: 'green',
    '--primary':      '#22c55e',
    '--primary-dark': '#16a34a',
    '--primary-rgb':  '34,197,94',
    '--accent':       '#34d399',
    '--bg':           '#001a0a',
    '--bg2':          '#022c12',
    '--bg3':          '#053d18',
    '--surface':      '#031a0c',
    '--surface2':     '#072415',
    '--border':       '#0a4a1c',
    '--text':         '#d1fae5',
    '--text-muted':   '#065f46',
    '--gradient':     'linear-gradient(135deg,#22c55e 0%,#34d399 100%)',
    '--glow':         '0 0 20px rgba(34,197,94,0.3)',
  },
};

// Shared semantic colours (same across all themes)
const SHARED = {
  '--success':    '#22c55e',
  '--success-bg': 'rgba(34,197,94,0.15)',
  '--warning':    '#f59e0b',
  '--warning-bg': 'rgba(245,158,11,0.15)',
  '--error':      '#ef4444',
  '--error-bg':   'rgba(239,68,68,0.15)',
  '--radius':     '12px',
  '--radius-sm':  '8px',
  '--radius-lg':  '18px',
};

function applyTheme(name) {
  const t = THEMES[name] || THEMES.blue;
  const root = document.documentElement;
  Object.entries({ ...t, ...SHARED }).forEach(([k, v]) => {
    if (k.startsWith('--')) root.style.setProperty(k, v);
  });
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem('sp_theme');
    return THEMES[saved] ? saved : 'blue';
  });

  const setTheme = (name) => {
    if (!THEMES[name]) return;
    setThemeState(name);
    localStorage.setItem('sp_theme', name);
    applyTheme(name);
  };

  // Apply on mount & whenever theme changes
  useEffect(() => { applyTheme(theme); }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
